<template>
  <div>
    <div class="hyp-icon-container">
      <div
        v-for="(item, index) in items"
        :key="index"
        class="pull-left hyp-icon-text"
        :class="{ 'mb-2': stacked }"
      >
        <v-avatar
          v-if="item.show"
          class="hyp-topic-item mx-1"
          :class="{ 'hyp-topic-item-disabled': item.disabled }"
          :size="30"
          :title="item.type.toUpperCase()"
          @click="toggle(item, $event)"
        >
          <img
            v-if="item.selected && item.imgSelected"
            :src="item.imgSelected"
            :alt="item.type"
            :class="{ 'hyp-stacked': stacked }"
          />
          <img
            v-if="!item.selected && item.img"
            :src="item.img"
            :alt="item.type"
            :class="{ 'hyp-stacked': stacked }"
          />
        </v-avatar>
      </div>

      <div class="clearfix"></div>
    </div>
    <div v-if="showTotals" class="hyp-text-container mt-1">
      <div
        v-for="(item, index) in items"
        :key="index"
        class="pull-left hyp-icon-text text-center"
      >
        <div v-if="item.show && item.hasOwnProperty('total')">
          {{ item.total }}
        </div>
      </div>
      <div class="clearfix"></div>
    </div>
  </div>
</template>

<script>
import lodashOrderBy from "lodash/orderBy";
import lodashForEach from "lodash/forEach";
import lodashUnion from "lodash/union";
import lodashPull from "lodash/pull";

import {
  getPrimaryTopics,
  getTopicsArray,
  getTopicKey,
} from "@/services/events/topics.service";
import { ICONS } from "@/common/constants/icons";
import { hasEssentialsAccess } from "@/services/user/user.service";

const EMITTED_ACTIONS = {
  CLICK: "click",
};

export default {
  name: "TopicsFilterBar",
  props: {
    selected: {
      type: Array,
      default: () => [],
    },
    shown: {
      type: Array,
      default: () => [],
    },
    totals: {
      type: Object,
    },
    showTotals: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideAllInfo: {
      type: Boolean,
      default: false,
    },
    stacked: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      items: [],
      selectedTypes: this.selected,
      shownTypes: this.shown,
      icons: ICONS,
      hasEssentials: hasEssentialsAccess(),
    };
  },
  mounted() {
    this.getItems();
  },
  methods: {
    getItems() {
      this.items = [];
      const TOPICS = getTopicsArray();
      const SORTED_TOPICS = lodashOrderBy(TOPICS, ["order"], ["asc"]);
      SORTED_TOPICS.forEach((topic) => {
        if (
          this.featureUnlocked(`topic-${topic.apiName}`) &&
          !topic.supportsVerified
        ) {
          let total = 0;
          let typeName = getTopicKey(topic.apiName);
          lodashForEach(this.totals, (value, key) => {
            if (
              key === typeName ||
              (topic.parentIds && topic.parentIds[0] === key)
            ) {
              total = value;
            }
          });
          let img, imgSelected;
          try {
            const primaryTypeName = topic.parentIds
              ? topic.parentIds[0]
              : typeName;
            img = require(`@/assets/images${this.icons.types[primaryTypeName].active}`);
            imgSelected = require(`@/assets/images${this.icons.types[primaryTypeName].selected}`);
          } catch (err) {
            img = require(`@/assets/images${this.icons.types["events_general"].active}`);
            imgSelected = require(`@/assets/images${this.icons.types["events_general"].selected}`);
          }
          let show =
            this.shownTypes.indexOf(typeName) > -1 ||
            (typeName === "health" && this.shownTypes.indexOf("covid19") > -1);
          let selected =
            this.selectedTypes.indexOf(typeName) > -1 ||
            (typeName === "health" && this.shownTypes.indexOf("covid19") > -1);
          this.items.push({
            type: typeName,
            img: img,
            imgSelected: imgSelected,
            selected: selected,
            show: show,
            total: this.getFormattedTotal(total),
            disabled: this.hasEssentials && topic.noEssentials,
          });
        }
      });

      let shownEventSelected = false;
      for (let i = 0; i < this.selectedTypes.length; i++) {
        if (this.shownTypes.indexOf(this.selectedTypes[i]) > -1) {
          shownEventSelected = true;
        }
      }
      if (!this.hideAllInfo) {
        this.items.push({
          type: "all",
          img: require(`@/assets/images/icons/events_allinfo_gray.png`),
          imgSelected: require(`@/assets/images/icons/events_allinfo_blue.png`),
          selected:
            this.selectedTypes.indexOf("all") > -1 || !shownEventSelected,
          show: this.shownTypes.indexOf("all") > -1,
        });
      }
    },
    getFormattedTotal(total) {
      const MILLION = 1000000;
      const THOUSAND = 1000;
      let formatted;
      if (total > MILLION) {
        formatted = parseFloat((total / MILLION).toFixed(1)) + "M";
      } else if (total > THOUSAND) {
        formatted = parseFloat((total / THOUSAND).toFixed(1)) + "K";
      } else {
        formatted = total;
      }
      return formatted;
    },
    toggle(item, event) {
      if (!this.disabled && !item.disabled) {
        item.selected = !item.selected;
        if (item.type === "all") {
          if (item.selected) {
            this.selectedTypes = ["all"];
            this.$emit(EMITTED_ACTIONS.CLICK, []);
          } else {
            const PRIMARY_TOPICS = getPrimaryTopics();
            PRIMARY_TOPICS.forEach((topic) => {
              if (this.featureUnlocked(`topic-${topic.type}`)) {
                this.selectedTypes.push(topic.type);
              }
            });
            this.selectedTypes = this.selectedTypes.filter((x) => x !== "all");
            this.$emit(EMITTED_ACTIONS.CLICK, this.selectedTypes);
          }
          this.items.forEach((i) => {
            if (i.type !== "all") {
              i.selected = !item.selected;
            }
          });
        } else {
          this.selectedTypes = this.selectedTypes.filter((x) => x !== "all");
          this.items.forEach((i) => {
            if (i.type === "all") {
              i.selected = false;
            }
          });
          if (item.selected) {
            this.selectedTypes = lodashUnion(this.selectedTypes, [item.type]);
          } else {
            lodashPull(this.selectedTypes, item.type);
          }
          this.$emit(EMITTED_ACTIONS.CLICK, this.selectedTypes);
        }
      }
    },
  },
  watch: {
    totals: {
      handler() {
        if (this.totals) {
          this.getItems();
        }
      },
      deep: true,
    },
    shown: {
      handler() {
        this.shownTypes = this.shown;
        this.getItems();
      },
    },
    selected: {
      handler() {
        this.selectedTypes = this.selected;
        this.getItems();
      },
    },
  },
  beforeDestroy() {
    this.items = [];
    this.selectedTypes = [];
  },
};
</script>

<style scoped>
.hyp-topic-item {
  cursor: pointer;
}
.hyp-topic-item-disabled {
  cursor: default;
  opacity: 0.35;
}
.hyp-icon-text {
  width: 40px;
  font-size: 11px;
}
.hyp-stacked {
  border: 1px solid lightgray;
}
</style>
